import { Button, Cell, DataTable, Icon, TableFooter, Tooltip } from 'bold-ui'
import { CheckboxField, CidadaoSelectFieldModel, DecimalField, NumberField, SimNaoEnum } from 'components/form'
import { SimNaoCheckboxGroupField } from 'components/form/field'
import { TableBox } from 'components/table/TableBox'
import { usePagination } from 'components/table/usePagination'
import { css } from 'emotion'
import { TipoAtividadeEnum } from 'graphql/types.generated'
import React from 'react'
import { MetaArray } from 'util/metaPath'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { CidadaoParticipanteModel, ProfissionalCpfCnsIdModel } from 'view/atividade-coletiva/model-atividadeColetiva'

import { ParticipanteCidadaoInfo } from './ParticipanteCidadaoInfo'
import { ParticipanteEditableTableForm } from './ParticipanteEditableTableForm'
import { ParticipanteImc } from './ParticipanteImc'
import { ParticipanteImcAlert } from './ParticipanteImcAlert'

export interface ParticipanteEditableTableModel {
  _id: ID
  isCidadaoParticipanteFormOpen: boolean
  cidadao: CidadaoSelectFieldModel
  cidadaoParticipante: CidadaoParticipanteModel
  peso: string
  altura: string
  avaliacaoAlterada: boolean
  pnctAbandonouGrupo: SimNaoEnum
  pnctCessouHabitoFumar: SimNaoEnum
}

type ParticipanteEditableTableRowModel = ParticipanteEditableTableModel & { index: number }

interface ParticipanteEditableTableProps {
  now: Date
  dataAtividade: LocalDate
  tipoAtividade: TipoAtividadeEnum
  required: boolean
  profissionalResponsavel: ProfissionalCpfCnsIdModel
  profissionaisEnvolvidos: ProfissionalCpfCnsIdModel[]
  isPraticaSaudePnct: boolean
  name: MetaArray<ParticipanteEditableTableModel>
}

export function ParticipanteEditableTable(props: ParticipanteEditableTableProps) {
  const { name, isPraticaSaudePnct, now, dataAtividade, tipoAtividade, ...formProps } = props

  const {
    handleSubmit,
    removeItem,
    input: { value },
  } = useEditableListField({ name })

  const allItems: ParticipanteEditableTableModel[] = value || []

  const tableRows: ParticipanteEditableTableRowModel[] = allItems.map((item, index) => ({ ...item, index })).reverse()

  const { paginatedItems, tableProps } = usePagination({ items: tableRows, initialPageSize: 5 })

  const getRowPath = (row: ParticipanteEditableTableRowModel) => name.get(row.index)

  const renderButtons = (row: ParticipanteEditableTableRowModel) => (
    <Tooltip text='Excluir'>
      <Button type='button' kind='normal' skin='ghost' size='small' onClick={() => removeItem(row)}>
        <Icon icon='trashOutline' />
      </Button>
    </Tooltip>
  )

  return (
    <TableBox
      header={
        <ParticipanteEditableTableForm
          now={now}
          allItems={allItems}
          dataAtividade={dataAtividade}
          tipoAtividade={tipoAtividade}
          onSubmit={handleSubmit}
          {...formProps}
        />
      }
    >
      <DataTable<ParticipanteEditableTableRowModel>
        rows={paginatedItems}
        columns={[
          {
            name: 'nome',
            header: 'Nome',
            render: (row) => <ParticipanteCidadaoInfo participante={row} dataAtividade={dataAtividade} />,
            style: styles.tableRowNome,
          },
          {
            name: 'peso',
            header: 'Peso (kg)',
            render: (row) => (
              <Cell style={styles.tableCell}>
                <DecimalField name={getRowPath(row).peso} min={0.5} max={500} maxLength={6} />
              </Cell>
            ),
            style: styles.tableRowNumber,
          },
          {
            name: 'altura',
            header: 'Altura (cm)',
            render: (row) => (
              <Cell style={styles.tableCell}>
                <NumberField name={getRowPath(row).altura} min={20} max={250} maxLength={3} />
              </Cell>
            ),
            style: styles.tableRowNumber,
          },
          {
            name: 'imc',
            header: 'IMC',
            render: (row) => <ParticipanteImc participante={row} />,
            style: styles.tableRowIMC,
          },
          {
            name: 'imcAlert',
            header: '',
            render: (row) => <ParticipanteImcAlert participante={row} dataAtividade={dataAtividade ?? now.getTime()} />,
            style: styles.tableRowIMCAlert,
          },
          {
            name: 'avaliacaoAlterada',
            header: 'Avaliação alterada',
            render: (row) => <CheckboxField name={getRowPath(row).avaliacaoAlterada} />,
            style: isPraticaSaudePnct ? styles.tableRowAvaliacao : styles.tableRowAvaliacaoSemPnct,
          },
          isPraticaSaudePnct && {
            name: 'pnctAbandonouGrupo',
            header: 'Abandonou o grupo?',
            render: (row) => <SimNaoCheckboxGroupField name={getRowPath(row).pnctAbandonouGrupo} />,
            style: styles.tableRowPnct,
          },
          isPraticaSaudePnct && {
            name: 'pnctCessouHabitoFumar',
            header: 'Cessou o hábito de fumar?',
            render: (row) => <SimNaoCheckboxGroupField name={getRowPath(row).pnctCessouHabitoFumar} />,
            style: styles.tableRowPnct,
          },
          {
            name: 'buttons',
            render: renderButtons,
            style: styles.tableRowButtons,
          },
        ].filterNotFalsy()}
      />
      <TableFooter style={styles.tableFooter} {...tableProps} />
    </TableBox>
  )
}

const styles = {
  tableFooter: css`
    border-top: 0;
  `,
  tableRowNome: css`
    width: 12rem;
  `,
  tableRowNumber: css`
    width: 6rem;
  `,
  tableRowIMC: css`
    width: 2rem;
  `,
  tableRowIMCAlert: css`
    width: 18rem;
    padding-left: 0;
  `,
  tableRowAvaliacao: css`
    width: 6rem;
    text-align: center;
  `,
  tableRowAvaliacaoSemPnct: css`
    width: 12rem;
    text-align: center;
  `,
  tableRowPnct: css`
    width: 4rem;
  `,
  tableRowButtons: css`
    width: 3.5rem;
    text-align: right;
  `,
  tableCell: css`
    padding: 0;
    width: 4.5rem;
  `,
}
